// app/apps/campsite-explorer/components/CampsiteActions.tsx
'use client';

import { BRAND, withBase } from '../lib/brand';
import React, { useState, useEffect } from 'react';
import { FaDirections, FaHeart, FaShare, FaPhone, FaFacebook, FaTwitter, FaWhatsapp, FaEnvelope, FaLinkedin, FaCopy, FaTimes, FaCalendarCheck } from 'react-icons/fa';
import { trackGenerateLead, trackShare } from '../lib/analytics';
import { apiSetSavedCampsite, isLoggedIn } from '../utils/tripApi';

interface CampsiteActionsProps {
  booking_url?: string;
  latitude?: string;
  longitude?: string;
  phone?: string;
  entityId?: number;
  entityName?: string;
  campsiteType?: string;
}

function trackBookingClick(
  clickType: 'booking_link' | 'phone',
  entityId?: number,
  entityName?: string,
  campsiteType?: string,
) {
  try {
    const payload = JSON.stringify({
      clickType,
      entityId,
      entityName: entityName || '',
      campsiteType: campsiteType || '',
    });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(
        'https://app.trip-planner.com.au/api/products/track-booking-click',
        new Blob([payload], { type: 'application/json' }),
      );
    } else {
      fetch('https://app.trip-planner.com.au/api/products/track-booking-click', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    // Never block the user
  }
}

export default function CampsiteActions({
  booking_url,
  latitude,
  longitude,
  phone,
  entityId,
  entityName,
  campsiteType,
}: CampsiteActionsProps) {
  const handleDirections = () => {
    if (latitude && longitude) {
      const mapUrl = `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`;
      window.open(mapUrl, '_blank');
    }
  };

  const [isSaved, setIsSaved] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);

  // Self-heal booking link: if a (possibly ISR-cached) page shows no booking_url
  // for a BOOKABLE type, verify against live data so a stale page cache can't
  // hide a Book button and cost a booking. No fetch for non-bookable sites.
  const [resolvedBookingUrl, setResolvedBookingUrl] = useState(booking_url);
  useEffect(() => {
    const bookable = ['accom', 'accommodation', 'tours', 'caravan_park'].includes(campsiteType || '');
    if (resolvedBookingUrl || !bookable || !entityId) return;
    let cancelled = false;
    fetch(`https://app.trip-planner.com.au/api/entities/${entityId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const u = (d?.entity || d)?.booking_url;
        if (!cancelled && u) setResolvedBookingUrl(u);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [resolvedBookingUrl, campsiteType, entityId]);

  // Check if campsite is already saved on component mount
  useEffect(() => {
    const savedCampsites = JSON.parse(localStorage.getItem('savedCampsites') || '[]');
    const currentUrl = window.location.href;
    setIsSaved(savedCampsites.some((campsite: any) => campsite.url === currentUrl));
  }, [entityId]);

  const handleShare = async () => {
    const shareUrl = window.location.href;
    const shareTitle = entityName || document.title;
    const shareText = `Check out ${shareTitle} on Camping Australia`;

    // Use native share on mobile only (desktop Chrome's native share
    // strips our branded email subject/body when sharing via Gmail)
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (isMobile && navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
        return;
      } catch (err) {
        console.log('Native share failed, showing fallback menu');
      }
    }

    // Show custom share menu with branded email
    setShowShareMenu(true);
  };

  const handleSocialShare = (platform: string) => {
    const url = encodeURIComponent(window.location.href);
    const name = entityName || document.title;
    const shortText = encodeURIComponent(`Check out ${name} on Camping Australia`);

    // Branded email body
    const emailSubject = encodeURIComponent(`${name} — found this on ${BRAND.name}!`);
    const emailBody = encodeURIComponent(
      `Hey,\n\n` +
      `I found this great spot and thought you might be interested:\n\n` +
      `${name}\n` +
      `${window.location.href}\n\n` +
      `You can check photos, reviews, weather forecast, nearby campsites and more on the listing.\n\n` +
      `— Shared via ${BRAND.name}\n` +
      `Discover thousands of campsites: ${BRAND.siteUrl}${withBase('')}`
    );

    const shareUrls: Record<string, string> = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      twitter: `https://twitter.com/intent/tweet?url=${url}&text=${shortText}`,
      whatsapp: `https://wa.me/?text=${shortText}%20${url}`,
      email: `mailto:?subject=${emailSubject}&body=${emailBody}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      copy: 'copy'
    };

    if (platform === 'copy') {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    } else if (platform === 'email') {
      // mailto: must use location.href, not window.open (Gmail ignores popups)
      window.location.href = shareUrls[platform];
    } else {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }

    // GA4: Track share
    trackShare({ entityId: entityId || 0, entityName: entityName || '', method: platform });

    setShowShareMenu(false);
  };

  const handleSave = () => {
    const savedCampsites = JSON.parse(localStorage.getItem('savedCampsites') || '[]');
    const currentUrl = window.location.href;
    const newSaved = !isSaved;
    setIsSaved(newSaved); // optimistic update

    if (!newSaved) {
      // Remove from saved
      const filtered = savedCampsites.filter((campsite: any) => campsite.url !== currentUrl);
      localStorage.setItem('savedCampsites', JSON.stringify(filtered));
    } else {
      // Add to saved
      const campsiteData = {
        name: document.title.split(' | ')[0],
        url: currentUrl,
        savedAt: new Date().toISOString(),
        latitude,
        longitude,
        phone,
        booking_url,
        entityId,
      };
      savedCampsites.push(campsiteData);
      localStorage.setItem('savedCampsites', JSON.stringify(savedCampsites));
    }

    // Sync to backend when logged in
    if (entityId && isLoggedIn()) {
      apiSetSavedCampsite(entityId, newSaved);
    }
  };

  // Booking-app button hierarchy: one hero CTA, everything else steps down.
  // Directions is the hero action only when there's nothing to book or call.
  const primaryCta =
    'w-full flex items-center justify-center gap-2 text-white text-center py-3.5 px-4 rounded-xl font-semibold text-[15px] bg-gradient-to-b from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 shadow-lg shadow-emerald-600/20 hover:shadow-emerald-600/30 hover:-translate-y-0.5 transition-all duration-200';
  const secondaryCta =
    'w-full flex items-center justify-center gap-2 bg-white border border-slate-300 text-slate-700 py-3 px-4 rounded-xl font-semibold hover:bg-slate-50 hover:border-slate-400 transition duration-200';
  const directionsIsPrimary = !resolvedBookingUrl && !phone;

  return (
    <div className="space-y-3">
      {resolvedBookingUrl && (
        <a
          href={resolvedBookingUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={primaryCta}
          onClick={() => {
            trackBookingClick('booking_link', entityId, entityName, campsiteType);
            trackGenerateLead({ entityId: entityId || 0, entityName: entityName || '', entityType: campsiteType, leadType: 'booking_link', bookingUrl: resolvedBookingUrl });
          }}
        >
          <FaCalendarCheck aria-hidden="true" />
          Book Online
        </a>
      )}

      {/* Without a booking link the phone on record is usually a general
          contact (park office, visitor centre) — many of these sites are
          free and unbookable, so promising "Call to Book" is dishonest.
          Offer the call neutrally instead. */}
      {!resolvedBookingUrl && phone && (
        <button
          className={primaryCta}
          onClick={() => {
            trackBookingClick('phone', entityId, entityName, campsiteType);
            trackGenerateLead({ entityId: entityId || 0, entityName: entityName || '', entityType: campsiteType, leadType: 'phone_reveal' });
            window.location.href = `tel:${phone}`;
          }}
        >
          <FaPhone aria-hidden="true" />
          Call campsite
        </button>
      )}

      {!resolvedBookingUrl && campsiteType === 'free_camping' && (
        <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2 text-center">
          Free campsite — no booking needed, sites are first come, first served.
        </p>
      )}

      {latitude && longitude && (
        <button
          onClick={handleDirections}
          className={directionsIsPrimary ? primaryCta : secondaryCta}
        >
          <FaDirections aria-hidden="true" />
          Get Directions
        </button>
      )}

      <div className="flex gap-3">
        <button
          onClick={handleSave}
          aria-pressed={isSaved}
          aria-label={isSaved ? 'Remove from saved campsites' : 'Save this campsite'}
          className={`flex-1 ${isSaved ? 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'} border py-2.5 px-3 rounded-xl text-sm font-medium transition duration-200 flex items-center justify-center`}
        >
          <FaHeart className={`mr-2 ${isSaved ? 'text-red-500' : 'text-slate-400'}`} aria-hidden="true" />
          {isSaved ? 'Saved' : 'Save'}
        </button>
        <button
          onClick={handleShare}
          aria-label="Share this campsite"
          className="flex-1 bg-white border border-slate-300 text-slate-600 py-2.5 px-3 rounded-xl text-sm font-medium hover:bg-slate-50 transition duration-200 flex items-center justify-center"
        >
          <FaShare className="mr-2 text-slate-400" aria-hidden="true" />
          Share
        </button>
      </div>

      {/* Share Menu Modal */}
      {showShareMenu && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" role="dialog" aria-modal="true" aria-label="Share campsite" onClick={() => setShowShareMenu(false)}>
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Share this campsite</h3>
              <button onClick={() => setShowShareMenu(false)} className="text-gray-500 hover:text-gray-700" aria-label="Close share menu">
                <FaTimes aria-hidden="true" />
              </button>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <button
                onClick={() => handleSocialShare('facebook')}
                className="flex flex-col items-center p-3 rounded-lg hover:bg-gray-100 transition"
              >
                <FaFacebook className="text-2xl text-[#1558b0] mb-1" />
                <span className="text-xs">Facebook</span>
              </button>
              
              <button
                onClick={() => handleSocialShare('twitter')}
                className="flex flex-col items-center p-3 rounded-lg hover:bg-gray-100 transition"
              >
                <FaTwitter className="text-2xl text-accent mb-1" />
                <span className="text-xs">Twitter</span>
              </button>
              
              <button
                onClick={() => handleSocialShare('whatsapp')}
                className="flex flex-col items-center p-3 rounded-lg hover:bg-gray-100 transition"
              >
                <FaWhatsapp className="text-2xl text-green-500 mb-1" />
                <span className="text-xs">WhatsApp</span>
              </button>
              
              <button
                onClick={() => handleSocialShare('email')}
                className="flex flex-col items-center p-3 rounded-lg hover:bg-gray-100 transition"
              >
                <FaEnvelope className="text-2xl text-gray-600 mb-1" />
                <span className="text-xs">Email</span>
              </button>
              
              <button
                onClick={() => handleSocialShare('linkedin')}
                className="flex flex-col items-center p-3 rounded-lg hover:bg-gray-100 transition"
              >
                <FaLinkedin className="text-2xl text-[#1558b0] mb-1" />
                <span className="text-xs">LinkedIn</span>
              </button>
              
              <button
                onClick={() => handleSocialShare('copy')}
                className="flex flex-col items-center p-3 rounded-lg hover:bg-gray-100 transition"
              >
                <FaCopy className="text-2xl text-gray-600 mb-1" />
                <span className="text-xs">Copy Link</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}